import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ClockState } from "./api";

export function useSession() {
  return useQuery({ queryKey: ["session"], queryFn: api.session, staleTime: Infinity });
}

export function useConfig(enabled: boolean) {
  return useQuery({ queryKey: ["config"], queryFn: api.config, staleTime: Infinity, enabled });
}

export function useBoard(date: string | undefined) {
  return useQuery({
    queryKey: ["board", date ?? "today"],
    queryFn: () => api.board(date),
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });
}

/**
 * Server-sent events: the server pushes a revision string whenever the feed, the plan or
 * the closures change. We refetch the board then, and keep the clock in sync.
 */
export function useLiveUpdates(onClock: (c: ClockState) => void, sandbox: number | null = null) {
  const qc = useQueryClient();
  const last = useRef<string>("");
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    // A sandbox has its own plan versions, so its own revisions: the stream follows the one on screen.
    const es = new EventSource(sandbox != null ? `/api/events?sandbox=${sandbox}` : "/api/events");
    es.addEventListener("open", () => setConnected(true));
    es.addEventListener("error", () => {
      setConnected(false);
      // The browser retries a dropped connection on its own; it gives up only when the
      // server answered with something other than a stream, which after a sign-out is a
      // 401. The stream is the first thing to notice, so it is the one to ask again.
      if (es.readyState === EventSource.CLOSED) void qc.invalidateQueries({ queryKey: ["session"] });
    });
    es.addEventListener("revision", (e) => {
      const data = JSON.parse((e as MessageEvent).data) as { revision: string; clock: ClockState };
      onClock(data.clock);
      if (last.current && last.current !== data.revision) {
        void qc.invalidateQueries({ queryKey: ["board"] });
      }
      last.current = data.revision;
    });
    return () => es.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, sandbox]);
  return connected;
}

/** Smoothly advancing clock between server updates (the replay clock can run at 60x). */
export function useNow(clock: ClockState | undefined): number {
  const anchor = useRef<{ server: number; local: number; speed: number; playing: boolean } | null>(null);
  const [, force] = useState(0);
  if (clock && (!anchor.current || Math.abs(anchor.current.server - clock.now) > 1 || anchor.current.speed !== clock.speed || anchor.current.playing !== clock.playing)) {
    anchor.current = { server: clock.now, local: Date.now(), speed: clock.speed, playing: clock.playing };
  }
  useEffect(() => {
    const id = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const a = anchor.current;
  if (!a) return Date.now();
  return a.playing ? a.server + (Date.now() - a.local) * a.speed : a.server;
}

/**
 * A value that follows its input after it has held still for `ms`. A preview that asks
 * the server on every keystroke of a note would ask a dozen times for one word.
 */
export function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return settled;
}

export function useLocalStorage<T>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  const set = (v: T) => {
    setValue(v);
    try {
      localStorage.setItem(key, JSON.stringify(v));
    } catch {
      /* storage unavailable */
    }
  };
  return [value, set];
}
